import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotifications,
  fetchNotificationsUnreadCount,
  markNotificationRead,
  markNotificationDismissed,
  type NotificationView,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';

/**
 * Notification hooks for the PWA bell + dedicated /notifications
 * page. Mirrors the admin-side architecture from Phase B commit
 * b827f60: a 30s-polled unread-count query at the shell level
 * (drives the Profile tab dot) plus a lazy list query the
 * /notifications page mounts.
 *
 * Both queries gate on auth via `enabled: isAuthenticated` so
 * the PWA doesn't fire 401s on the login screen. The shell-level
 * unread-count is mounted in AppShell once and shared across
 * every route via React Query's cache.
 */

const POLL_MS = 30_000;
const STALE_MS = 5_000;

/**
 * Polled count of unread, non-dismissed notifications targeted at
 * the caller. Drives the Profile tab dot indicator. Keep this
 * query small + cheap — it fires every 30s for every authed
 * driver, so changes here multiply across the platform.
 *
 * `.catch(() => 0)` swallows transient errors so a single 401 /
 * 5xx flicker doesn't make the badge disappear and reappear.
 * Surfacing failures via the console is enough for triage; the
 * UX cost of a flapping badge isn't worth it.
 */
export function useNotificationsUnreadCount(): number {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const q = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => fetchNotificationsUnreadCount().catch(() => 0),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: STALE_MS,
    enabled: isAuthenticated,
  });
  return q.data ?? 0;
}

/**
 * Full list query for the /notifications page. Lazy: fetched on
 * page mount, not on every shell render. Hard cap at 50 for v1
 * — pagination is filed as polish.
 */
export function useNotifications(opts?: { limit?: number }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const limit = opts?.limit ?? 50;
  return useQuery<NotificationView[]>({
    queryKey: ['notifications', 'list', limit],
    queryFn: () => fetchNotifications({ limit }),
    staleTime: 10_000,
    enabled: isAuthenticated,
  });
}

/**
 * Optimistic mark-read mutation. Stamps `read_at` on the row in
 * the cache before the API round-trip; on error, rolls back
 * to the previous list. The unread-count cache is also nudged
 * down by 1 so the Profile tab dot updates instantly.
 */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onMutate: async (id: string) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: ['notifications', 'list'] }),
        qc.cancelQueries({ queryKey: ['notifications', 'unread-count'] }),
      ]);
      const prevList = qc.getQueriesData<NotificationView[]>({ queryKey: ['notifications', 'list'] });
      const prevCount = qc.getQueryData<number>(['notifications', 'unread-count']) ?? 0;
      // List: stamp read_at on the row (if currently unread).
      qc.setQueriesData<NotificationView[]>(
        { queryKey: ['notifications', 'list'] },
        (old) => old?.map((n) => (
          n._id === id && n.read_at === null
            ? { ...n, read_at: new Date().toISOString() }
            : n
        )),
      );
      // Count: decrement, floor at 0.
      qc.setQueryData(['notifications', 'unread-count'], Math.max(0, prevCount - 1));
      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (!ctx) return;
      for (const [key, value] of ctx.prevList) {
        qc.setQueryData(key, value);
      }
      qc.setQueryData(['notifications', 'unread-count'], ctx.prevCount);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });
}

/**
 * Optimistic mark-dismissed mutation. Removes the row from the
 * list cache immediately; rolls back on error. Also decrements
 * the unread count if the dismissed notification was unread.
 */
export function useMarkNotificationDismissed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markNotificationDismissed,
    onMutate: async (id: string) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: ['notifications', 'list'] }),
        qc.cancelQueries({ queryKey: ['notifications', 'unread-count'] }),
      ]);
      const prevList = qc.getQueriesData<NotificationView[]>({ queryKey: ['notifications', 'list'] });
      const prevCount = qc.getQueryData<number>(['notifications', 'unread-count']) ?? 0;
      // Find whether the dismissed row was unread before removing.
      let wasUnread = false;
      for (const [, value] of prevList) {
        const found = value?.find((n) => n._id === id);
        if (found && found.read_at === null) {
          wasUnread = true;
          break;
        }
      }
      qc.setQueriesData<NotificationView[]>(
        { queryKey: ['notifications', 'list'] },
        (old) => old?.filter((n) => n._id !== id),
      );
      if (wasUnread) {
        qc.setQueryData(['notifications', 'unread-count'], Math.max(0, prevCount - 1));
      }
      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (!ctx) return;
      for (const [key, value] of ctx.prevList) {
        qc.setQueryData(key, value);
      }
      qc.setQueryData(['notifications', 'unread-count'], ctx.prevCount);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
